import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { API_URL } from "../../constants";
import { track } from "../../utils/analytics";
import { authHeader } from "../../utils/auth";
import { ExportLibraryLoadError, loadPngExporter } from "../../utils/exportLibraries";
import { cleanPhotoName } from "../../utils/photos";

/**
 * Text-ready snapshots: portrait (4:5) image cards built from the listing's
 * own photos — a hero shot under an ink scrim with one line that sells it —
 * sized for texting a seller or posting to Instagram/social. Rendered
 * client-side and exported at 3× via html-to-image; Share hands the PNG to
 * the phone's share sheet (Messages, Instagram, …), elsewhere it downloads.
 *
 * The same photo-first "ink panel" look as the deck and one-pager, so
 * everything an agent sends reads as one kit.
 */

const Wrap = styled.div`
  margin: 26px 0 8px;
`;

// --- The card itself (what gets rasterized) ---
const CARD_W = 264;
const CARD_H = 330; // 4:5, Instagram portrait

const Card = styled.div`
  position: relative;
  width: ${CARD_W}px;
  height: ${CARD_H}px;
  border-radius: 14px;
  overflow: hidden;
  background: #0b1c1e;
  flex-shrink: 0;
  box-shadow: 0 10px 30px rgba(11, 28, 30, 0.18);
`;

const CardPhoto = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Lightweight, tasteful looks — plain CSS filters, captured 1:1 in the export.
export const SNAPSHOT_FILTERS = [
  { key: "natural", label: "Natural", css: "none" },
  { key: "warm", label: "Warm", css: "saturate(1.15) sepia(0.12) brightness(1.03)" },
  { key: "bright", label: "Bright", css: "brightness(1.12) saturate(1.08) contrast(1.02)" },
  { key: "crisp", label: "Crisp", css: "contrast(1.12) saturate(1.05)" },
  { key: "mono", label: "Mono", css: "grayscale(1) contrast(1.05)" },
];

const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const SmallButton = styled.button`
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? "#0e7e7a" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0e7e7a" : "#ffffff")};
  color: ${(p) => (p.$primary ? "#ffffff" : "#374151")};

  &:hover:not(:disabled) {
    background: ${(p) => (p.$primary ? "#0b6360" : "#f3f4f6")};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CardsRow = styled.div`
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;
`;

// Batch actions get their own centered, breathing row — they act on the whole
// gallery, so they sit apart from the per-card buttons.
const BatchRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 30px;
  padding-top: 22px;
  border-top: 1px solid #e5e7eb;
`;

// Two-layer text protection: a whisper of full-frame darkening (keeps even a
// bright, unfiltered sky from washing the top), then a tall bottom gradient
// that goes nearly solid where the text sits.
const CardVeil = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(11, 28, 30, 0.14);
`;

const CardScrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 78%;
  background: linear-gradient(
    to bottom,
    rgba(11, 28, 30, 0),
    rgba(11, 28, 30, 0.38) 32%,
    rgba(11, 28, 30, 0.78) 62%,
    rgba(11, 28, 30, 0.97) 100%
  );
`;

const CardLogoChip = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: #ffffff;
  border-radius: 6px;
  padding: 5px 8px;
  display: inline-block;

  img {
    height: 16px;
    width: auto;
    display: block;
  }
`;

// Bottom-anchored, but bounded at the top (clear of the logo chip) so a long
// line reflows inside a known box instead of growing past the card edge.
const CardContent = styled.div`
  position: absolute;
  left: 20px;
  right: 20px;
  top: 44px;
  bottom: 18px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: #ffffff;
`;

const CardKicker = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 8px;
`;

const CardAddress = styled.div`
  font-family: var(--font-heading, Poppins), Georgia, serif;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 8px;
  text-shadow: 0 1px 3px rgba(11, 28, 30, 0.55);
`;

const CardRule = styled.div`
  width: 44px;
  height: 3px;
  background: ${(p) => p.$accent};
  margin-bottom: 9px;
`;

const CardLine = styled.div`
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 12px;
  text-shadow: 0 1px 2px rgba(11, 28, 30, 0.5);
`;

const CardBy = styled.div`
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.75);
`;

// --- Controls ---

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 14px;
`;

const VariantChip = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? "#0e7e7a" : "#d1d5db")};
  background: ${(p) => (p.$active ? "#e6f4f3" : "#ffffff")};
  color: ${(p) => (p.$active ? "#0b605d" : "#374151")};
`;

const ThumbRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 16px;
`;

const ThumbStar = styled.span`
  position: absolute;
  top: 2px;
  right: 3px;
  font-size: 10px;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(11, 28, 30, 0.8);
`;

const Thumb = styled.button`
  position: relative;
  width: 56px;
  height: 42px;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${(p) => (p.$active ? "#0e7e7a" : "transparent")};
  background: #f3f4f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ShareButton = styled.button`
  min-height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #0e7e7a;
  background: #0e7e7a;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #0b605d;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const DownloadLinkButton = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 14px auto 0;
  max-width: 560px;
  text-align: center;
`;

const ExportError = styled.p`
  margin: 12px auto 0;
  max-width: 560px;
  color: #b91c1c;
  font-size: 0.86rem;
  font-weight: 600;
  text-align: center;
`;

/** @typedef {{key: string, label: string, kicker: string, line: string}} SnapshotVariant */

/** @param {string | number | null | undefined} n */
const fmtShort = (n) => {
  const v = Number(n);
  if (!v || Number.isNaN(v)) return "";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2).replace(/0$/, "").replace(/\.$/, "")}M`;
  return `$${Math.round(v / 1000)}K`;
};

// Feature assets often arrive as full sentences ("Premium chef's kitchen
// equipped with a Sub-Zero refrigerator and Thermador range.") — on a photo
// card that becomes a wall of text. Keep the lead phrase, drop the spec sheet.
// Every cut here lands on a phrase boundary: a card that trails off in "…"
// reads like a broken export, so an asset with no clean boundary near the
// front is left whole for fitSnapshotLine to bound and snapshotLineStyle to
// typeset.
const ASSET_CUT_WORDS = [
  " equipped with",
  " featuring",
  " designed to",
  " complete with",
  " boasting",
  " offering",
  " with ",
];

/** @param {unknown} raw */
export const tightenAsset = (raw) => {
  const t = String(raw || "")
    .trim()
    .replace(/[.!]+$/, "");
  if (t.length <= 60) return t;
  const comma = t.indexOf(", ");
  if (comma > 15 && comma < 60) return t.slice(0, comma);
  const lower = t.toLowerCase();
  for (const w of ASSET_CUT_WORDS) {
    const i = lower.indexOf(w);
    if (i > 15 && i < 60) return t.slice(0, i);
  }
  return t;
};

// How many highlights ride on one card — past this budget the photo loses,
// so later highlights are dropped whole rather than half-shown.
const LINE_BUDGET = 110;

/**
 * The first whole sentence of a passage; the passage itself if it has none.
 * The terminator must be followed by a space, or the decimal in "3.5 baths"
 * ends the sentence and the card reads "…this 4 bed, 3."
 */
/** @param {unknown} text */
const firstSentence = (text) => {
  const t = String(text || "").trim();
  if (!t) return "";
  const m = t.match(/^.{20,180}?[.!?](?=\s|$)/);
  return m ? m[0] : t;
};

// The text block inside the card is 224×268px and the card clips whatever
// overflows it, so an unbounded line does not "shrink to fit" — past roughly
// 600 characters the smallest type step still overflows and the top of the
// line is cut off mid-word, behind the logo chip. Bound the line at a length
// the ladder demonstrably sets whole (measured against the longest address
// step), and cut back to a clean boundary rather than trailing off in a "…".
const SNAPSHOT_LINE_MAX = 200;

// Below this a cut-back line has stopped saying anything about the home, so a
// coarser boundary is preferred over a very short one.
const SNAPSHOT_LINE_MIN = 60;

/** Index just past the last match of `pattern` in `text`, or -1. */
/** @param {string} text @param {RegExp} pattern */
const lastBoundary = (text, pattern) => {
  let end = -1;
  for (const m of text.matchAll(pattern)) end = /** @type {number} */ (m.index) + m[0].length;
  return end;
};

/**
 * Bound a snapshot line to what the card can hold, ending it on a boundary: a
 * finished sentence first, then a highlight separator, then a clause, and a
 * word break only as a floor. Nothing is ever appended — the ellipsis is what
 * made an otherwise fine card look like a broken export.
 * @param {unknown} raw
 */
export const fitSnapshotLine = (raw) => {
  const t = String(raw || "")
    .replace(/\s+/g, " ")
    .trim();
  if (t.length <= SNAPSHOT_LINE_MAX) return t;
  const head = t.slice(0, SNAPSHOT_LINE_MAX);
  for (const pattern of [/[.!?](?=\s)/g, /\s·(?=\s)/g, /[,;:](?=\s)/g, /\s(?=\S)/g]) {
    const end = lastBoundary(head, pattern);
    if (end >= SNAPSHOT_LINE_MIN) return head.slice(0, end).replace(/[\s,;:·]+$/, "");
  }
  return head.trimEnd();
};

/**
 * The card is a fixed 4:5 raster with nowhere for a long line to go, so it is
 * typeset to fit: each step down keeps every word on the card and inside the
 * bounded text block. Only the first three steps are reachable from
 * fitSnapshotLine; the rest are a backstop for any other caller.
 */
const LINE_TYPE_STEPS = [
  { maxChars: 90, fontSize: 13.5, lineHeight: 1.45 },
  { maxChars: 140, fontSize: 12.5, lineHeight: 1.4 },
  { maxChars: 200, fontSize: 11.5, lineHeight: 1.36 },
  { maxChars: 280, fontSize: 10.5, lineHeight: 1.32 },
  { maxChars: 420, fontSize: 9.5, lineHeight: 1.3 },
  { maxChars: Number.POSITIVE_INFINITY, fontSize: 8.5, lineHeight: 1.28 },
];

/** @param {unknown} line @returns {{fontSize: string, lineHeight: number}} */
export const snapshotLineStyle = (line) => {
  const length = String(line || "").length;
  const step =
    LINE_TYPE_STEPS.find((s) => length <= s.maxChars) ||
    LINE_TYPE_STEPS[LINE_TYPE_STEPS.length - 1];
  return { fontSize: `${step.fontSize}px`, lineHeight: step.lineHeight };
};

// A long address is the other way the card runs out of room: unit numbers and
// spelled-out parkways push it to three lines at the display size.
const ADDRESS_TYPE_STEPS = [
  { maxChars: 34, fontSize: 18 },
  { maxChars: 52, fontSize: 16 },
  { maxChars: Number.POSITIVE_INFINITY, fontSize: 14 },
];

/** @param {unknown} address @returns {{fontSize: string}} */
export const snapshotAddressStyle = (address) => {
  const length = String(address || "").length;
  const step =
    ADDRESS_TYPE_STEPS.find((s) => length <= s.maxChars) ||
    ADDRESS_TYPE_STEPS[ADDRESS_TYPE_STEPS.length - 1];
  return { fontSize: `${step.fontSize}px` };
};

/**
 * Build the snapshot variants — BUYER-facing social posts (a prospective buyer
 * should see one and want to reach out), so every line comes from publishable
 * listing copy: the facts, the feature highlights, the listing description. Never the
 * report's internal analysis, advice, or pricing verdicts.
 */
/**
 * @param {Record<string, any>} content
 * @param {string | null | undefined} address
 * @returns {SnapshotVariant[]}
 */
export const snapshotVariants = (content, address) => {
  const pd = content?.property_details || {};
  const sections = /** @type {Record<string, any>[]} */ (content?.sections || []);
  const rewrite = sections.find((s) => s.__type__ === "ListingRewriteSection");
  const facts = [
    pd.bedrooms && `${pd.bedrooms} bd`,
    pd.bathrooms && `${pd.bathrooms} ba`,
    pd.square_feet && `${Number(pd.square_feet).toLocaleString()} sq ft`,
    pd.listing_price && fmtShort(pd.listing_price),
  ]
    .filter(Boolean)
    .join(" · ");
  const variants = /** @type {SnapshotVariant[]} */ ([]);
  if (facts)
    variants.push({ key: "listed", label: "Just listed", kicker: "Just Listed", line: facts });
  // NOT the cover tagline — it describes the REPORT ("a strategic roadmap
  // to...") and reads absurd on a buyer-facing post. Feature highlights are
  // property language.
  const exec = sections.find((s) => s.__type__ === "ExecutiveSummarySection");
  const tightened = /** @type {unknown[]} */ (
    exec?.key_assets || rewrite?.neighborhood_highlights || []
  )
    .map(tightenAsset)
    .filter(Boolean)
    .slice(0, 3);
  const highlights = [];
  for (const h of tightened) {
    const candidate = [...highlights, h].join(" · ");
    if (highlights.length && candidate.length > LINE_BUDGET) break;
    highlights.push(h);
  }
  if (highlights.length)
    variants.push({
      key: "highlights",
      label: "The highlights",
      kicker: "Welcome Home",
      line: highlights.join(" · "),
    });
  const peek = firstSentence(rewrite?.rewritten_description);
  if (peek)
    variants.push({
      key: "peek",
      label: "From the listing",
      kicker: "Take a Look Inside",
      line: peek,
    });
  if (!variants.length)
    variants.push({
      key: "listed",
      label: "Just listed",
      kicker: "Just Listed",
      line: address || "",
    });
  // Bounded here rather than at each source, so no future line can reach the
  // card at a length the card cannot show.
  return variants.map((v) => ({ ...v, line: fitSnapshotLine(v.line) }));
};

/**
 * The listing photos ordered for the snapshot picker: the AI's photogenic
 * picks first (chosen by Gemini at generation time), then the sequence lead,
 * then the rest in manifest order.
 */
/**
 * @param {Record<string, any>} content
 * @param {Record<string, string>} manifest
 */
export const orderedSnapshotPhotos = (content, manifest) => {
  const names = Object.keys(manifest || {});
  const picks = /** @type {string[]} */ (content?.cover_page_data?.social_photo_picks || [])
    .map(cleanPhotoName)
    .filter((n) => names.includes(n));
  const seq = /** @type {Record<string, any>[]} */ (content?.sections || []).find(
    (s) => s.__type__ === "PhotoSequenceSection"
  );
  const lead = cleanPhotoName(seq?.strategy?.lead_image);
  const ordered = [...picks];
  if (lead && names.includes(lead) && !ordered.includes(lead)) ordered.push(lead);
  for (const n of names) if (!ordered.includes(n)) ordered.push(n);
  return ordered;
};

/** The default snapshot photo: the AI's top pick, else sequence lead, else first. */
/**
 * @param {Record<string, any>} content
 * @param {Record<string, string>} manifest
 */
export const defaultSnapshotPhoto = (content, manifest) =>
  orderedSnapshotPhotos(content, manifest)[0] || null;

/**
 * @param {{
 *   uuid: string,
 *   doc: Record<string, any>,
 *   onShared?: () => void,
 * }} props
 */
const ShareSnapshots = ({ uuid, doc, onShared }) => {
  const content = useMemo(() => doc.content || {}, [doc.content]);
  const manifest = /** @type {Record<string, string>} */ (doc.photo_manifest || {});
  const branding = doc.branding || {};
  const accent = branding.accent_color || "#0e7e7a";
  const variants = useMemo(() => snapshotVariants(content, doc.address), [content, doc.address]);
  const [photoName, setPhotoName] = useState(() => defaultSnapshotPhoto(content, manifest));
  const [filterKey, setFilterKey] = useState("natural");
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));
  const [exportError, setExportError] = useState("");
  const cardRefs = useRef(/** @type {Record<string, HTMLElement | null>} */ ({}));
  const flightRef = useRef(/** @type {object | null} */ (null));
  const exportScope = JSON.stringify([
    uuid,
    doc.version,
    doc.photo_access_token,
    photoName,
    filterKey,
  ]);
  const scopeRef = useRef(exportScope);
  scopeRef.current = exportScope;
  useEffect(() => {
    if (scopeRef.current !== exportScope) return;
    flightRef.current = null;
    setBusyKey(null);
    setExportError("");
    return () => {
      flightRef.current = null;
    };
  }, [exportScope]);

  if (!photoName) return null; // no listing photos — nothing to snapshot
  const filter = SNAPSHOT_FILTERS.find((f) => f.key === filterKey) || SNAPSHOT_FILTERS[0];
  // Proxied through the API so the canvas export gets CORS-clean pixels.
  // <img> tags can't send Authorization headers, so proxy reads carry the
  // signed capability the report-document GET issued.
  const accessToken = doc.photo_access_token || "";
  /** @param {string} name */
  const photoSrc = (name) =>
    `${API_URL}/computations/${uuid}/photo/${encodeURIComponent(name)}/${accessToken ? `?pt=${encodeURIComponent(accessToken)}` : ""}`;
  const listedBy = [branding.agent_name, branding.brokerage].filter(Boolean).join(" · ");
  const slug = (doc.address || "listing").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const orderedPhotos = orderedSnapshotPhotos(content, manifest);
  const thumbs = orderedPhotos.slice(0, 8);
  const aiPicks = new Set((content?.cover_page_data?.social_photo_picks || []).map(cleanPhotoName));

  /** @param {Record<string, string | number | boolean>} properties */
  const trackSnapshot = (properties) => {
    track("share_snapshot", {
      ...properties,
      computation_uuid: uuid,
      document_version: doc.version || 0,
    });
  };

  /** @param {string} key @param {() => boolean} isCurrent */
  const renderVariant = async (key, isCurrent) => {
    const toPng = await loadPngExporter();
    if (!isCurrent()) return null;
    const node = cardRefs.current[key];
    if (!node) return null;
    const dataUrl = await toPng(node, { pixelRatio: 3, cacheBust: true, skipFonts: true });
    if (!isCurrent()) return null;
    const response = await fetch(dataUrl);
    if (!isCurrent()) return null;
    const blob = await response.blob();
    if (!isCurrent()) return null;
    return { dataUrl, file: new File([blob], `${slug}-${key}.png`, { type: "image/png" }) };
  };

  /**
   * @param {string} dataUrl
   * @param {string} filename
   */
  const download = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
  };

  /** @param {string} key */
  const beginExport = (key) => {
    if (flightRef.current) return null;
    const flight = {};
    flightRef.current = flight;
    const authorization = authHeader().Authorization;
    setExportError("");
    setBusyKey(key);
    return {
      isCurrent: () =>
        flightRef.current === flight &&
        scopeRef.current === exportScope &&
        authHeader().Authorization === authorization,
      finish: () => {
        if (flightRef.current === flight) {
          flightRef.current = null;
          setBusyKey(null);
        }
      },
    };
  };
  /** @param {unknown} error */
  const exportFailure = (error) =>
    setExportError(
      error instanceof ExportLibraryLoadError
        ? error.message
        : "We couldn't prepare that image. Check that the listing photo loads, then try again."
    );

  /**
   * @param {SnapshotVariant} variant
   * @param {boolean} share
   */
  const exportOne = async (variant, share) => {
    const flight = beginExport(variant.key);
    if (!flight) return;
    try {
      const out = await renderVariant(variant.key, flight.isCurrent);
      if (!flight.isCurrent()) return;
      if (!out) {
        exportFailure(null);
        return;
      }
      if (share && navigator.canShare && navigator.canShare({ files: [out.file] })) {
        try {
          await navigator.share({ files: [out.file], title: doc.address || "Listing" });
          if (!flight.isCurrent()) return;
          if (onShared) onShared();
          trackSnapshot({ variant: variant.key, method: "share" });
          return;
        } catch {
          /* cancelled or unsupported — fall through to download */
        }
      }
      if (!flight.isCurrent()) return;
      trackSnapshot({ variant: variant.key, method: "download" });
      download(out.dataUrl, out.file.name);
    } catch (error) {
      if (flight.isCurrent()) exportFailure(error);
    } finally {
      flight.finish();
    }
  };

  /** @param {boolean} share */
  const exportAll = async (share) => {
    const flight = beginExport(share ? "all-share" : "all-download");
    if (!flight) return;
    try {
      const outs = [];
      for (const v of variants) {
        const out = await renderVariant(v.key, flight.isCurrent);
        if (!flight.isCurrent()) return;
        if (out) outs.push(out);
      }
      if (!outs.length) {
        exportFailure(null);
        return;
      }
      const files = outs.map((o) => o.file);
      if (share && navigator.canShare && navigator.canShare({ files })) {
        try {
          await navigator.share({ files, title: doc.address || "Listing" });
          if (!flight.isCurrent()) return;
          if (onShared) onShared();
          trackSnapshot({
            variant: "all",
            method: "share",
            count: outs.length,
          });
          return;
        } catch {
          /* cancelled or unsupported — fall through to downloads */
        }
      }
      if (!flight.isCurrent()) return;
      trackSnapshot({
        variant: "all",
        method: "download",
        count: outs.length,
      });
      for (const out of outs) download(out.dataUrl, out.file.name);
    } catch (error) {
      if (flight.isCurrent()) exportFailure(error);
    } finally {
      flight.finish();
    }
  };

  return (
    <Wrap>
      {thumbs.length > 1 && (
        <ThumbRow aria-label="Snapshot photo">
          {thumbs.map((name) => (
            <Thumb
              key={name}
              type="button"
              $active={name === photoName}
              aria-label={`Use ${name}`}
              disabled={busyKey != null}
              onClick={() => setPhotoName(name)}
            >
              <img src={manifest[name]} alt="" loading="lazy" />
              {aiPicks.has(name) && <ThumbStar aria-hidden="true">★</ThumbStar>}
            </Thumb>
          ))}
        </ThumbRow>
      )}
      <ChipRow aria-label="Photo look">
        {SNAPSHOT_FILTERS.map((f) => (
          <VariantChip
            key={f.key}
            type="button"
            $active={f.key === filter.key}
            disabled={busyKey != null}
            onClick={() => setFilterKey(f.key)}
          >
            {f.label}
          </VariantChip>
        ))}
      </ChipRow>

      <CardsRow>
        {variants.map((variant) => (
          <CardColumn key={variant.key}>
            <Card
              ref={(node) => {
                cardRefs.current[variant.key] = node;
              }}
              aria-label={`Snapshot: ${variant.label}`}
            >
              <CardPhoto
                src={photoSrc(photoName)}
                alt=""
                crossOrigin="anonymous"
                style={{ filter: filter.css }}
              />
              <CardVeil />
              <CardScrim />
              {branding.logo_url && branding.show_logo !== false && (
                <CardLogoChip>
                  <img src={branding.logo_url} alt="" />
                </CardLogoChip>
              )}
              <CardContent>
                <CardKicker>{variant.kicker}</CardKicker>
                <CardAddress style={snapshotAddressStyle(doc.address)}>
                  {doc.address || ""}
                </CardAddress>
                <CardRule $accent={accent} />
                <CardLine style={snapshotLineStyle(variant.line)}>{variant.line}</CardLine>
                {listedBy && <CardBy>Listed by {listedBy}</CardBy>}
              </CardContent>
            </Card>
            <CardActions>
              <SmallButton
                type="button"
                $primary
                disabled={busyKey != null}
                onClick={() => exportOne(variant, true)}
              >
                {busyKey === variant.key ? "Preparing…" : canShare ? "Share" : "Save"}
              </SmallButton>
              <SmallButton
                type="button"
                disabled={busyKey != null}
                onClick={() => exportOne(variant, false)}
              >
                PNG
              </SmallButton>
            </CardActions>
          </CardColumn>
        ))}
      </CardsRow>

      {variants.length > 1 && (
        <BatchRow>
          <ShareButton type="button" disabled={busyKey != null} onClick={() => exportAll(true)}>
            {busyKey === "all-share" ? "Preparing…" : canShare ? "Share all" : "Save all"}
          </ShareButton>
          <DownloadLinkButton
            type="button"
            disabled={busyKey != null}
            onClick={() => exportAll(false)}
          >
            {busyKey === "all-download" ? "Preparing…" : "Download all"}
          </DownloadLinkButton>
        </BatchRow>
      )}
      {exportError && <ExportError role="alert">{exportError}</ExportError>}
      <Hint>
        Sized for texting and Instagram (4:5). Share opens your phone's share sheet — send straight
        to Messages or the Instagram app. Pick a photo and a look; every card updates.
      </Hint>
    </Wrap>
  );
};

export default ShareSnapshots;
